import HistorialCambios from "components/HistorialCambios";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useContext, useEffect, useState, useCallback, useMemo } from "react";

// Components
import { Card, Grid, Icon, Menu, MenuItem } from "@mui/material";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import ModalDelete from "components/Modals/Delete";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import DataTable from "components/Tables/DataTable";
import SoftButton from "components/SoftButton";
import CustomPagination from "components/Pagination";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";

import { MessageManager } from "context";
import { UserContext } from "context/user";
import { getColor } from "utils/colors";
import ModalResponsableTecnico from "./components/ModalResponsableTecnico";
import dataUsuarios from "./data/dataUsuarios";

// Hook de debounce
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

function ResponsableTecnico() {
  const [rows, setRows] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [search, setSearch] = useState("");

  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [selectedResponsableTecnico, setSelectedResponsableTecnico] = useState(null);
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({ pages: 0, total: 0 });
  const [orderBy, setOrderBy] = useState({ campo: "nombre", orden: "ASC" });
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [filter, setFilter] = useState({});

  const { handleSnackbar } = useContext(MessageManager);
  const { verificarAcceso } = useContext(UserContext);
  const { columns, getRows } = dataUsuarios;

  // Funciones con useCallback
  const handleOrderBy = useCallback((campo) => {
    setOrderBy((prev) => ({
      campo,
      orden: prev.campo === campo && prev.orden === "ASC" ? "DESC" : "ASC",
    }));
  }, []);

  const handleOpenMenu = useCallback((event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedResponsableTecnico(item);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
    setOpenMenu(false);
  }, []);

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setPage(1);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const [deleteResponsableTecnico] = useMutation(
    gql`
      mutation deleteResponsableTecnico($id: ID!) {
        deleteResponsableTecnico(id: $id)
      }
    `
  );
  const [resendResponsableTecnicoCredentials] = useMutation(gql`
    mutation resendResponsableTecnicoCredentials($id: ID!) {
      resendResponsableTecnicoCredentials(id: $id)
    }
  `);

  const handleResendCredentials = async () => {
    try {
      const { data } = await resendResponsableTecnicoCredentials({
        variables: { id: selectedResponsableTecnico.id },
      });
      handleSnackbar(
        data.resendResponsableTecnicoCredentials
          ? `Credenciales enviadas a ${selectedResponsableTecnico.email}`
          : "No se pudo enviar el correo; el error quedó registrado",
        data.resendResponsableTecnicoCredentials ? "success" : "error"
      );
    } catch (e) {
      handleSnackbar(e.message || "No se pudieron reenviar las credenciales", "error");
    }
  };

  const { loading, error, data, refetch } = useQuery(
    gql`
      query getResponsablesTecnicos($search: String, $page: Int, $order: Order, $filter: JSON) {
        paginationInfo {
          pages
          total
        }
        responsablesTecnicos(
          search: $search
          pagination: { page: $page, limit: 10 }
          order: $order
          filter: $filter
        ) {
          id
          nombre
          apellido
          dni
          matricula
          telefono
          email
        }
      }
    `,
    {
      variables: {
        search: search || null,
        page: page,
        order: { field: orderBy.campo, order: orderBy.orden },
        filter: { ...filter },
      },
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  // useMemo para recalcular las filas solo cuando data o la función getRows cambien
  const memoizedRows = useMemo(() => {
    if (data) {
      return getRows(data.responsablesTecnicos, handleOpenMenu);
    }
    return [];
  }, [data, getRows, handleOpenMenu]);

  useEffect(() => {
    if (data) {
      setRows(memoizedRows);
      setPagesInfo(data.paginationInfo);
    }
  }, [data, memoizedRows]);

  const handleDeleteResponsableTecnico = async (id) => {
    try {
      await deleteResponsableTecnico({ variables: { id } });
      refetch();
      handleSnackbar("Responsable Técnico eliminado correctamente", "success");
    } catch (e) {
      handleSnackbar(e.message || "Error al eliminar el Responsable Técnico", "error");
    }
  };

  // Render del menú contextual
  const renderMenu = (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      <MenuItem disabled={!verificarAcceso(233)}
        onClick={() => {
          setOpenModalAdd(true);
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftTypography variant="p">Editar</SoftTypography>
          <Icon fontSize="small" sx={{ verticalAlign: "middle", color: getColor("gobSecondary") }}>
            edit
          </Icon>
        </SoftBox>
      </MenuItem>
      <MenuItem disabled={!verificarAcceso(245)}
        onClick={() => {
          handleResendCredentials();
          handleCloseMenu();
        }}
      >
        <SoftBox display="flex" justifyContent="space-between" sx={{ width: "100%" }}>
          <SoftTypography variant="p">Reenviar credenciales</SoftTypography>
          <Icon fontSize="small" color="primary">email</Icon>
        </SoftBox>
      </MenuItem>
      <MenuItem disabled={!verificarAcceso(234)}
        onClick={() => {
          setOpenModalDelete(true);
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftTypography variant="p">Eliminar</SoftTypography>
          <Icon fontSize="small" color="error">
            delete
          </Icon>
        </SoftBox>
      </MenuItem>
    <HistorialCambios modulo="responsables" id={selectedResponsableTecnico?.id} nombre={[selectedResponsableTecnico?.nombre, selectedResponsableTecnico?.apellido].filter(Boolean).join(" ")} menu />
    </Menu>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox>
          <Card>
            <Grid container spacing={2} p={3}>
              <Grid item xs={12}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                  <SoftBox display="flex" alignItems="center">
                    <SoftBox mr={1} display="flex" alignItems="center">
                      <SoftBox
                        display="grid"
                        justifyContent="center"
                        alignItems="center"
                        bgColor="primary"
                        color="white"
                        width="3rem"
                        height="3rem"
                        shadow="md"
                        borderRadius="lg"
                        variant="gradient"
                      >
                        <Icon fontSize="default">hail</Icon>
                      </SoftBox>
                    </SoftBox>
                    <SoftBox>
                      <SoftTypography variant="h6" fontWeight="bold">
                        Responsables Técnicos
                      </SoftTypography>
                      <SoftTypography variant="caption" fontWeight="regular">
                        {pagesInfo?.total
                          ? `${pagesInfo?.total} responsable${pagesInfo?.total > 1 ? "s" : ""}`
                          : "No hay responsables técnicos"}
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>
                  <SoftBox display="flex" alignItems="center" gap={2}>
                    <SoftInput
                      placeholder="Escribe aquí..."
                      icon={{ component: "search", direction: "left" }}
                      onChange={handleSearch}
                    />
                    <SoftButton
                      disabled={!verificarAcceso(232)}
                      color="primary"
                      onClick={() => setOpenModalAdd(true)}
                      startIcon={<Icon>add</Icon>}
                    >
                      Nuevo
                    </SoftButton>
                  </SoftBox>
                </SoftBox>
              </Grid>
            </Grid>
            <SoftBox>
              {loading ? (
                <Loading />
              ) : rows?.length > 0 ? (
                <DataTable columns={columns} rows={rows} order={orderBy} setOrder={handleOrderBy} />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={data?.responsablesTecnicos.length}
              total={pagesInfo?.total}
              pages={pagesInfo?.pages}
              page={page}
              setPage={setPage}
              noCard
            />
          </Card>
          {renderMenu}
          <ModalResponsableTecnico
            open={openModalAdd}
            handleClose={() => {
              setOpenModalAdd(false);
              setSelectedResponsableTecnico(null);
            }}
            refetch={refetch}
            idResponsableTecnico={selectedResponsableTecnico?.id}
          />
          <ModalDelete
            open={openModalDelete}
            handleClose={() => setOpenModalDelete(false)}
            nombre={`el responsable técnico ${selectedResponsableTecnico?.nombre}`}
            functionDelete={() => {
              handleDeleteResponsableTecnico(selectedResponsableTecnico.id);
              setOpenModalDelete(false);
            }}
          />
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ResponsableTecnico;
